import React, { use, useState } from "react"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Cell,
} from "recharts"
import { useMarketIntelligence, BRANCHES } from "./MarketIntelligenceContext"
import { authHeaders } from "../../utils/authFetch"

const C = {
  espresso: "transparent",
  dark: "#F9FAFB",
  card: "#FFFFFF",
  border: "#E5E7EB",
  muted: "#9CA3AF",
  text: "#111827",
  textSoft: "#6B7280",
  amber: "#D97706",
  gold: "#B45309",
  teal: "#0D9488",
  coral: "#DC2626",
  blue: "#2563EB",
  green: "#16A34A",
  purple: "#7C3AED",
}

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border p-5 ${className}`}
    style={{ background: C.card, borderColor: C.border }}
  >
    {children}
  </div>
)

const SectionTitle = ({ title, subtitle }) => (
  <div className="mb-5">
    <h2 className="m-0 text-[17px] font-bold tracking-tight" style={{ color: C.text }}>
      {title}
    </h2>
    {subtitle && (
      <p className="m-0 text-xs mt-0.5" style={{ color: C.textSoft }}>
        {subtitle}
      </p>
    )}
  </div>
)

const KPI = ({ label, value, sub, color = C.amber }) => (
  <div
    className="rounded-xl p-3.5 min-w-0 border"
    style={{ backgroundColor: "#fff", borderColor: C.border }}
  >
    <p className="m-0 text-[11px] uppercase tracking-widest" style={{ color: C.muted }}>
      {label}
    </p>
    <p className="mt-1 mb-0.5 text-[22px] font-bold" style={{ color }}>
      {value}
    </p>
    {sub && (
      <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>
        {sub}
      </p>
    )}
  </div>
)

const Shimmer = ({ w = "100%", h = 14, radius = 6 }) => (
  <div
    style={{
      width: w, height: h, borderRadius: radius,
      background: "linear-gradient(90deg, #F3F4F6 25%, #E5E7EB 37%, #F3F4F6 63%)",
      backgroundSize: "400% 100%",
      animation: "mi-shimmer 1.4s ease infinite",
    }}
  />
)

const SkeletonStyles = () => (
  <style>{`
    @keyframes mi-shimmer {
      0% { background-position: 100% 50%; }
      100% { background-position: 0% 50%; }
    }
  `}</style>
)

const CompetitorSkeleton = () => (
  <div className="flex flex-col gap-3">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="flex items-center gap-3">
        <Shimmer w={36} h={36} radius={999} />
        <div className="flex-1 flex flex-col gap-1.5">
          <Shimmer w="40%" h={12} />
          <Shimmer w="60%" h={10} />
        </div>
        <Shimmer w={48} h={14} />
      </div>
    ))}
  </div>
)

const FeedbackSkeleton = () => (
  <div>
    <div className="grid grid-cols-3 gap-2.5 mb-5">
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="rounded-xl p-3.5 border" style={{ borderColor: C.border }}>
          <Shimmer w="60%" h={10} />
          <div className="mt-2"><Shimmer w="40%" h={20} /></div>
          <div className="mt-1.5"><Shimmer w="50%" h={10} /></div>
        </div>
      ))}
    </div>
    <Shimmer w="30%" h={10} />
    <div className="flex flex-col divide-y mt-2" style={{ borderColor: C.border }}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="py-2.5 first:pt-0 last:pb-0 flex flex-col gap-1.5">
          <div className="flex items-center justify-between">
            <Shimmer w={70} h={16} radius={999} />
            <Shimmer w={60} h={16} radius={999} />
          </div>
          <Shimmer w="90%" h={12} />
          <Shimmer w="25%" h={10} />
        </div>
      ))}
    </div>
  </div>
)

const SwotSkeleton = () => (
    <div className="grid grid-cols-2 gap-3">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="rounded-xl p-3.5 border" style={{ borderColor: C.border }}>
        <div className="flex items-center gap-1.5 mb-2.5">
          <Shimmer w={8} h={8} radius={999} />
          <Shimmer w="50%" h={10} />
        </div>
        <div className="flex flex-col gap-2.5">
          {Array.from({ length: 3 }).map((_, j) => (
            <div key={j} className="flex flex-col gap-1.5">
              <Shimmer h={10} />
              <Shimmer w="70%" h={10} />
              <Shimmer w={80} h={14} radius={999} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)

const MenuGapSkeleton = () => (
    <div className="grid grid-cols-2 gap-3">
    {Array.from({ length: 4 }).map((_, i) => (
      <div key={i} className="rounded-xl p-3.5 border" style={{ borderColor: C.border }}>
        <div className="flex items-center gap-1.5 mb-2.5">
          <Shimmer w={8} h={8} radius={999} />
          <Shimmer w="50%" h={10} />
        </div>
        <div className="flex flex-col gap-2.5">
          {Array.from({ length: 3 }).map((_, j) => (
            <div key={j} className="flex flex-col gap-1.5">
              <Shimmer h={10} />
              <Shimmer w="70%" h={10} />
              <Shimmer w={80} h={14} radius={999} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
)

const Tag = ({ label, color = "amber" }) => {
  const styles = {
    amber: { background: "#FEF3C7", color: "#92400E" },
    teal: { background: "#CCFBF1", color: "#134E4A" },
    coral: { background: "#FEE2E2", color: "#991B1B" },
    blue: { background: "#DBEAFE", color: "#1E3A8A" },
    green: { background: "#DCFCE7", color: "#14532D" },
    gray: { background: C.dark, color: C.textSoft, border: `1px solid ${C.border}` },
  }
  return (
    <span
      className="inline-block text-[10px] px-2 py-0.5 rounded-full"
      style={styles[color] ?? styles.gray}
    >
      {label}
    </span>
  )
}

const SentimentPill = ({ sentiment }) => {
  const map = {
    Positive: { background: "#DCFCE7", color: "#14532D" },
    Negative: { background: "#FEE2E2", color: "#991B1B" },
    Neutral: { background: C.dark, color: C.textSoft, border: `1px solid ${C.border}` },
  }
  return (
    <span
      className="text-[10px] px-2 py-0.5 rounded-full"
      style={map[sentiment] ?? map.Neutral}
    >
      {sentiment}
    </span>
  )
}

const ConfidenceBadge = ({ confidence }) => {
  const map = {
    "DATA-BACKED": { bg: "#DCFCE7", color: "#14532D", dot: "#16A34A" },
    "INFERRED": { bg: "#DBEAFE", color: "#1E3A8A", dot: "#2563EB" },
    "SUGGESTED": { bg: "#FEF3C7", color: "#92400E", dot: "#D97706" },
  }
  const style = map[confidence] ?? map["INFERRED"]
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-medium"
      style={{ background: style.bg, color: style.color }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: style.dot }} />
      {confidence}
    </span>
  )
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null
  return (
    <div className="rounded-lg px-3 py-2 text-xs border" style={{ background: "#fff", borderColor: C.border }}>
      <p className="m-0 mb-1.5 font-semibold" style={{ color: C.text }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="m-0 my-0.5" style={{ color: p.color || C.textSoft }}>
          {p.name}: <strong>{p.value}</strong>
        </p>
      ))}
    </div>
  )
}

const RangeSelector = ({ range, setRange }) => {
  const options = [
    { label: "7 days", value: "7d" },
    { label: "30 days", value: "30d" },
    { label: "90 days", value: "90d" },
    { label: "1 year", value: "1y" },
  ]
  return (
    <div className="flex gap-1.5">
      {options.map((o) => (
        <button
          key={o.value}
          onClick={() => setRange(o.value)}
          className="px-3 py-1 rounded-full text-[11px] border transition-colors"
          style={{
            background: range === o.value ? C.amber : C.card,
            borderColor: range === o.value ? C.amber : C.border,
            color: range === o.value ? "#fff"  : C.textSoft,
            cursor: "pointer",
          }}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}

const GenerateButton = ({ onClick, loading, label = "Generate with AI" }) => (
  <button
    onClick={onClick}
    disabled={loading}
    className="px-4 py-2 rounded-lg text-[12px] font-semibold flex items-center gap-2"
    style={{
      background: loading ? C.border : C.amber,
      color: loading ? C.muted  : "#fff",
      cursor: loading ? "not-allowed" : "pointer",
      border: "none",
    }}
  >
    {loading ? (
      <>
        <span className="w-3 h-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
        Analyzing...
      </>
    ) : (
      <>✦ {label}</>
    )}
  </button>
)

const EmptyState = ({ message, onGenerate, loading }) => (
  <div
    className="flex flex-col items-center justify-center py-10 gap-3 rounded-xl border-2 border-dashed"
    style={{ borderColor: C.border }}
  >
    <p className="text-[13px] m-0" style={{ color: C.textSoft }}>{message}</p>
    <GenerateButton onClick={onGenerate} loading={loading} />
  </div>
)

const BranchSelector = ({ selectedBranch, setSelectedBranch }) => (
  <select
    value={selectedBranch.branchId}
    onChange={(e) => {
      const branch = BRANCHES.find((b) => b.branchId === e.target.value)
      if (branch) setSelectedBranch(branch)
    }}
    className="px-3 py-1.5 rounded-lg text-[12px] border"
    style={{
      background: C.card,
      borderColor: C.border,
      color: C.text,
      cursor: "pointer",
      outline: "none",
    }}
  >
    {BRANCHES.map((b) => (
      <option key={b.branchId} value={b.branchId}>
        {b.branchName}
      </option>
    ))}
  </select>
)

const HistoryCard = ({ entry, isActive, onClick }) => {
  const date = new Date(entry.generated_at)
  return (
    <button
      onClick={onClick}
      className="text-left rounded-xl border p-3 transition-colors flex-shrink-0"
      style={{
        width: 200,
        background: isActive ? C.amber + "10" : C.card,
        borderColor: isActive ? C.amber : C.border,
        cursor: "pointer",
      }}
    >
      <p className="m-0 text-[11px] font-semibold" style={{ color: isActive ? C.amber : C.text }}>
        {date.toLocaleDateString("en-PH", { month: "short", day: "numeric", year: "numeric" })}
      </p>
      <p className="m-0 text-[10px] mt-0.5" style={{ color: C.muted }}>
        {date.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
      </p>
      <div className="flex gap-1.5 mt-2 flex-wrap">
        <Tag label={`${entry.kpis?.competitorsTracked ?? "—"} competitors`} color="gray" />
        <Tag label={`${entry.kpis?.menuGapsFound ?? "—"} gaps`} color="gray" />
      </div>
    </button>
  )
}

const HistoryPanel = ({ history, loading, lastUpdated, onSelect, open }) => {
  if (!open) return null
  return (
    <Card>
      <SectionTitle title="Past Snapshots" subtitle="Click a snapshot to view it below · auto-removed after 30 days (Google Places policy)" />
      {loading ? (
        <div className="flex gap-3">
          {Array.from({ length: 4 }).map((_, i) => <Shimmer key={i} w={200} h={84} radius={12} />)}
        </div>
      ) : history.length === 0 ? (
        <p className="text-[13px] m-0" style={{ color: C.textSoft }}>No past snapshots yet for this branch.</p>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {history.map((entry) => (
            <HistoryCard
              key={entry.id}
              entry={entry}
              isActive={lastUpdated && new Date(entry.generated_at).getTime() === lastUpdated.getTime()}
              onClick={() => onSelect(entry)}
            />
          ))}
        </div>
      )}
    </Card>
  )
}

function getInitials(name = "") {
  return name.split(" ").slice(0, 2).map((w) => w[0]).join("").toUpperCase()
}

const AVATAR_COLORS = [
  { bg: "#FEF3C7", fg: "#92400E" },
  { bg: "#CCFBF1", fg: "#134E4A" },
  { bg: "#DBEAFE", fg: "#1E3A8A" },
  { bg: "#FEE2E2", fg: "#991B1B" },
  { bg: "#DCFCE7", fg: "#14532D" },
  { bg: "#EDE9FE", fg: "#5B21B6" },
]

const CompetitorTracker = ({ competitors, loading }) => {
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 6

  const totalPages = competitors ? Math.ceil(competitors.length / PAGE_SIZE) : 0
  const paginated = competitors ? competitors.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE) : []

  return (
    <Card>
      <SectionTitle title="Competitor Tracker" subtitle="Real-time data from Google Places API" />
      <div className="flex items-center justify-between mb-4">
        <div
          className="inline-flex items-center gap-1.5 text-[10px] px-2.5 py-1 rounded-full border"
          style={{ background: C.dark, borderColor: C.border, color: C.textSoft }}
        >
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="10" fill="#4285F4" />
            <circle cx="12" cy="12" r="3" fill="#fff" />
          </svg>
          Live · Google Places API
        </div>
        <a
        
          href="https://www.google.com/maps"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[10px]"
          style={{ color: C.textSoft, textDecoration: "none" }}
        >
          Powered by Google
        </a>
      </div>

      {!loading && competitors && competitors.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
          <KPI
            label="Avg Rating"
            value={
              competitors.filter(c => c.rating).length > 0
                ? `${(competitors.reduce((s, c) => s + (c.rating || 0), 0) / competitors.filter(c => c.rating).length).toFixed(1)} ★`
                : "—"
            }
            sub="across tracked competitors"
            color={C.amber}
          />
          <KPI
            label="Reviews Tracked"
            value={competitors.reduce((s, c) => s + (c.reviewCount || 0), 0).toLocaleString()}
            sub="total review volume"
            color={C.blue}
          />
          <KPI
            label="Currently Open"
            value={`${competitors.filter(c => c.isOpenNow).length}/${competitors.length}`}
            sub="open right now"
            color={C.green}
          />
        </div>
      )}

       {loading ? (
          <CompetitorSkeleton />
        ) : !competitors || competitors.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 gap-2 rounded-xl border-2 border-dashed" style={{ borderColor: C.border }}>
              <p className="text-[13px] m-0" style={{ color: C.textSoft }}>No competitor data yet. Use Generate Intelligence above.</p>
          </div>
        ) : (
        <>
          <div className="flex flex-col divide-y" style={{ borderColor: C.border }}>
            {paginated.map((c, i) => {
              const avatar = AVATAR_COLORS[(page * PAGE_SIZE + i) % AVATAR_COLORS.length]
              return (
                <div key={c.placeId || c.name} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
                  <div
                    className="w-9 h-9 rounded-full flex items-center justify-center text-[12px] font-semibold flex-shrink-0"
                    style={{ background: avatar.bg, color: avatar.fg }}
                  >
                    {getInitials(c.name)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="m-0 text-[13px] font-semibold" style={{ color: C.text }}>{c.name}</p>
                    <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>
                      {c.reviewCount} reviews
                      {c.isOpenNow !== null && (
                        <span className="ml-2 font-medium" style={{ color: c.isOpenNow ? C.green : C.coral }}>
                          · {c.isOpenNow ? "Open now" : "Closed"}
                        </span>
                      )}
                    </p>
                    <div className="flex flex-wrap gap-1 mt-1">
                      <Tag label={`Silent Loyalty: ${c.silentLoyaltyIndicator}`} color="gray" />
                      {c.reputationScore > 9 && <Tag label="High Threat" color="coral" />}
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p
                      className="m-0 text-[13px] font-bold"
                      style={{ color: c.rating >= 4.5 ? C.amber : c.rating >= 4.0 ? C.teal : C.coral }}
                    >
                      {c.rating ? `${c.rating} ★` : "N/A"}
                    </p>
                    <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>{c.priceLabelDisplay}</p>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-3 border-t" style={{ borderColor: C.border }}>
              <p className="m-0 text-[11px]" style={{ color: C.muted }}>
                Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + PAGE_SIZE, competitors.length)} of {competitors.length}
              </p>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="w-7 h-7 rounded-lg flex items-center justify-center border text-[11px]"
                  style={{
                    background: page === 0 ? C.dark : C.card,
                    borderColor: C.border,
                    color: page === 0 ? C.muted : C.text,
                    cursor: page === 0 ? "not-allowed" : "pointer",
                  }}
                >
                  ‹
                </button>
                {Array.from({ length: totalPages }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i)}
                    className="w-7 h-7 rounded-lg flex items-center justify-center border text-[11px] font-medium"
                    style={{
                      background: page === i ? C.amber : C.card,
                      borderColor: page === i ? C.amber : C.border,
                      color: page === i ? "#fff" : C.textSoft,
                      cursor: "pointer",
                    }}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page === totalPages - 1}
                  className="w-7 h-7 rounded-lg flex items-center justify-center border text-[11px]"
                  style={{
                    background: page === totalPages - 1 ? C.dark : C.card,
                    borderColor: C.border,
                    color: page === totalPages - 1 ? C.muted : C.text,
                    cursor: page === totalPages - 1 ? "not-allowed" : "pointer",
                  }}
                >
                  ›
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </Card>
  )
}

const CustomerFeedback = ({ feedbackSummary, loading }) => {
   if (loading) {
    return (
      <Card>
        <SectionTitle title="Customer Feedback" subtitle="Consolidated from Google reviews · tagged by theme" />
        <FeedbackSkeleton />
      </Card>
    )
  }
  if (!feedbackSummary) {
    return (
      <Card>
        <SectionTitle title="Customer Feedback" subtitle="Consolidated from Google reviews · tagged by theme" />
        <div className="flex flex-col items-center justify-center py-10 gap-2 rounded-xl border-2 border-dashed" style={{ borderColor: C.border }}>
          <p className="text-[13px] m-0" style={{ color: C.textSoft }}>No feedback data yet. Use Generate Intelligence above.</p>
        </div>
      </Card>
    )
  }

  const { positivePct, neutralPct, negativePct, recentFeedback } = feedbackSummary
  return (
    <Card>
      <SectionTitle title="Customer Feedback" subtitle="Consolidated from Google reviews · tagged by theme" />
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-5">
        <KPI label="Positive" value={`${positivePct}%`} sub="of reviews" color={C.green} />
        <KPI label="Neutral"  value={`${neutralPct}%`}  sub="of reviews" color={C.textSoft} />
        <KPI label="Negative" value={`${negativePct}%`} sub="of reviews" color={C.coral} />
      </div>
      <p className="text-[11px] uppercase tracking-widest mb-2 m-0" style={{ color: C.muted }}>
        Recent feedback
      </p>
      <div className="flex flex-col divide-y" style={{ borderColor: C.border }}>
        {(recentFeedback || []).map((f, i) => (
          <div key={i} className="py-2.5 first:pt-0 last:pb-0">
            <div className="flex items-center justify-between mb-1">
              <Tag label={f.topic} color={f.topicCategory || "gray"} />
              <SentimentPill sentiment={f.sentiment} />
            </div>
            <p className="m-0 text-[12px] leading-relaxed" style={{ color: C.textSoft }}>
              "{f.text}"
            </p>
            <p className="m-0 text-[11px] mt-1" style={{ color: C.muted }}>{f.source}</p>
          </div>
        ))}
      </div>
    </Card>
  )
}

const SWOT_STYLE = {
  strengths: { label: "Strengths", dotColor: "#16A34A", labelColor: "#15803D", bg: "#EAF3DE" },
  weaknesses: { label: "Weaknesses", dotColor: "#D85A30", labelColor: "#C2410C", bg: "#FAECE7" },
  opportunities:{ label: "Opportunities",dotColor: "#2563EB", labelColor: "#1D4ED8", bg: "#EFF6FF" },
  threats: { label: "Threats", dotColor: "#DC2626", labelColor: "#B91C1C", bg: "#FEE2E2" },
}

const SwotQuadrant = ({ data }) => (
  <div className="rounded-xl p-3.5 border" style={{ background: data.bg, borderColor: C.border }}>
    <div className="flex items-center gap-1.5 mb-2.5">
      <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: data.dotColor }} />
      <span className="text-[10px] font-bold uppercase tracking-widest" style={{ color: data.labelColor }}>
        {data.label}
      </span>
    </div>
    <div className="flex flex-col gap-2">
      {data.items.map((item, i) => (
        <div key={i} className="flex flex-col gap-1">
          <p
            className="m-0 text-[12px] leading-snug pl-2.5 border-l-2"
            style={{ color: C.textSoft, borderColor: data.dotColor + "66" }}
          >
            {item.text}
          </p>
          <div className="pl-2.5">
            <ConfidenceBadge confidence={item.confidence} />
          </div>
        </div>
      ))}
    </div>
  </div>
)

const SwotAnalysis = ({ swot, loading, onGenerate }) => (
  <Card>
    <div className="flex items-center justify-between mb-5">
      <SectionTitle
        title="SWOT Analysis"
        subtitle="Generated by Gemini 2.5 Flash · Google Places API + Owner Inputs"
      />
      <GenerateButton onClick={onGenerate} loading={loading} label="Regenerate" />
    </div>
     {loading ? (
        <SwotSkeleton />
      ) : !swot ? (
        <div className="flex flex-col items-center justify-center py-10 gap-2 rounded-xl border-2 border-dashed" style={{ borderColor: C.border }}>
            <p className="text-[13px] m-0" style={{ color: C.textSoft }}>Click Generate Intelligence above to create your SWOT analysis.</p>
        </div>
      ) : (
      <>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {Object.entries(SWOT_STYLE).map(([key, style]) => (
            <SwotQuadrant key={key} data={{ ...style, items: swot[key] || [] }} />
          ))}
        </div>
        <div className="flex items-center gap-4 mt-3 pt-3 border-t" style={{ borderColor: C.border }}>
          <p className="text-[10px] m-0" style={{ color: C.muted }}>Confidence key:</p>
          <ConfidenceBadge confidence="DATA-BACKED" />
          <ConfidenceBadge confidence="INFERRED" />
          <p className="text-[10px] m-0 ml-auto" style={{ color: C.muted }}>
            Source: Google Places API + Owner inputs
          </p>
        </div>
      </>
    )}
  </Card>
)

const PRIORITY_COLOR = { Urgent: "coral", Add: "green", Consider: "blue", Monitor: "gray" }
const CATEGORY_COLOR = { Coffee: "amber", "Non-Coffee": "teal", Food: "green", Seasonal: "blue" }

const MenuGapTable = ({ menuGaps, loading }) => {
   if (loading) {
    return (
      <Card>
        <SectionTitle
          title="Menu Gap Identification"
          subtitle="Items popular at competitors but absent or underrepresented in our menu"
        />
        <MenuGapSkeleton />
      </Card>
    )
  }
  if (!menuGaps || menuGaps.length === 0) {
    return (
      <Card>
        <SectionTitle
          title="Menu Gap Identification"
          subtitle="Items popular at competitors but absent or underrepresented in our menu"
        />
      <div className="flex flex-col items-center justify-center py-10 gap-2 rounded-xl border-2 border-dashed" style={{ borderColor: C.border }}>
          <p className="text-[13px] m-0" style={{ color: C.textSoft }}>No menu gaps identified yet. Use Generate Intelligence above.</p>
      </div>
      </Card>
    )
  }

  return (
    <Card>
      <SectionTitle
        title="Menu Gap Identification"
        subtitle="Items popular at competitors but absent or underrepresented in our menu"
      />
      <div className="overflow-x-auto">
        <table className="w-full text-[12px] border-collapse">
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {["Category", "Gap Item", "Competitors Offering", "Customer Demand", "Opportunity", "Priority"].map((h) => (
                <th
                  key={h}
                  className="text-left pb-2.5 pr-4 last:pr-0 text-[10px] uppercase tracking-widest font-semibold"
                  style={{ color: C.muted }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {menuGaps.map((g, i) => (
              <tr
                key={i}
                style={{ borderBottom: i < menuGaps.length - 1 ? `1px solid ${C.border}` : "none" }}
              >
                <td className="py-2.5 pr-4">
                  <Tag label={g.category} color={CATEGORY_COLOR[g.category] || "gray"} />
                </td>
                <td className="py-2.5 pr-4 font-semibold" style={{ color: C.text }}>{g.item}</td>
                <td className="py-2.5 pr-4" style={{ color: C.textSoft }}>{g.competitorsOffering}</td>
                <td className="py-2.5 pr-4">
                  <div className="flex items-center gap-2">
                    <div className="w-20 h-1.5 rounded-full" style={{ background: C.border }}>
                      <div
                        className="h-1.5 rounded-full"
                        style={{
                          width: `${g.demandPct}%`,
                          background: g.demandPct >= 80 ? C.amber : g.demandPct >= 60 ? C.teal : C.blue,
                        }}
                      />
                    </div>
                    <span style={{ color: C.textSoft }}>{g.demandLevel}</span>
                  </div>
                </td>
                <td
                  className="py-2.5 pr-4 font-bold text-[13px]"
                  style={{ color: g.opportunityScore >= 9 ? C.amber : g.opportunityScore >= 7 ? C.teal : C.blue }}
                >
                  {g.opportunityScore}
                </td>
                <td className="py-2.5">
                  <Tag label={g.priority} color={PRIORITY_COLOR[g.priority] || "gray"} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

const OpportunityChart = ({ menuGaps }) => {
  if (!menuGaps || menuGaps.length === 0) return null
  const data = menuGaps
    .map((g) => ({
      name:  g.item.length > 16 ? g.item.slice(0, 14) + "…" : g.item,
      score: g.opportunityScore,
      color: g.opportunityScore >= 9 ? C.amber : g.opportunityScore >= 7 ? C.teal : C.blue,
    }))
    .sort((a, b) => b.score - a.score)

  return (
    <Card>
      <SectionTitle title="Opportunity Scores" subtitle="Ranked menu gap priorities at a glance" />
      <ResponsiveContainer debounce={200} width="100%" height={220}>
        <BarChart data={data} layout="vertical" margin={{ top: 0, right: 20, left: 10, bottom: 0 }}>
          <CartesianGrid stroke={C.border} strokeDasharray="3 3" horizontal={false} />
          <XAxis type="number" domain={[0, 10]} tick={{ fill: C.muted, fontSize: 10 }} axisLine={false} tickLine={false} />
          <YAxis dataKey="name" type="category" tick={{ fill: C.textSoft, fontSize: 11 }} axisLine={false} tickLine={false} width={110} />
          <Tooltip content={<CustomTooltip />} />
          <Bar dataKey="score" name="Score" radius={[0, 4, 4, 0]}>
            {data.map((entry, i) => <Cell key={i} fill={entry.color} fillOpacity={0.85} />)}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Card>
  )
}

const SilentLoyaltyCard = ({ competitors }) => {
  if (!competitors || competitors.length === 0) return null
  return (
    <Card>
      <SectionTitle title="Silent Loyalty Analysis" subtitle="Competitors with likely high silent regular bases" />
      <div className="flex flex-col gap-2">
        {[...competitors]
          .sort((a, b) => a.silentLoyaltyIndicator - b.silentLoyaltyIndicator)
          .slice(0, 4)
          .map((c) => (
            <div
              key={c.placeId}
              className="flex items-center justify-between py-2 border-b last:border-0"
              style={{ borderColor: C.border }}
            >
              <div>
                <p className="m-0 text-[13px] font-semibold" style={{ color: C.text }}>{c.name}</p>
                <p className="m-0 text-[11px]" style={{ color: C.textSoft }}>
                  {c.reviewCount} total reviews · {c.silentLoyaltyIndicator} reviews/month
                </p>
              </div>
              <div className="text-right">
                <Tag
                  label={
                    c.silentLoyaltyIndicator < 3 ? "High Silent Base"
                    : c.silentLoyaltyIndicator < 8 ? "Moderate"
                    : "Review-Active"
                  }
                  color={
                    c.silentLoyaltyIndicator < 3 ? "coral"
                    : c.silentLoyaltyIndicator < 8 ? "amber"
                    : "teal"
                  }
                />
              </div>
            </div>
          ))}
      </div>
      <p className="text-[10px] mt-3 m-0" style={{ color: C.muted }}>
        [INFERRED] Low reviews/month + high total reviews = strong silent regular base.
        These competitors are harder to disrupt.
      </p>
    </Card>
  )
}


const AIDisclaimerBanner = ({ generatedAt }) => {
  const [dismissed, setDismissed] = useState(false)
  if (dismissed) return null
  return (
    <div className="rounded-xl px-4 py-3 border flex items-start gap-3" style={{ background: "#FFFBEB", borderColor: "#FDE68A" }}>
      <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5" style={{ background: "#FEF3C7" }}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
          <path d="M12 2L2 19h20L12 2z" stroke="#D97706" strokeWidth="2" strokeLinejoin="round" />
          <path d="M12 9v5M12 16.5v.5" stroke="#D97706" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </div>
      <div className="flex-1 min-w-0">
        <p className="m-0 text-[12px] font-semibold" style={{ color: "#92400E" }}>
          AI-Generated Content — For Reference Only
        </p>
        <p className="m-0 text-[11px] mt-0.5 leading-relaxed" style={{ color: "#B45309" }}>
          The insights, SWOT analysis, competitor assessments, and menu gap recommendations on this page are
          generated by an AI system (Google Gemini 2.5 Flash) using publicly available data from the Google
          Places API and owner-provided inputs. These outputs are pattern-based inferences, not verified facts.
          They are intended as decision-support reference points only and{" "}
          <strong>should not be acted upon without validation against your own direct business observation and judgment.</strong>{" "}
          KaPerk and its developers are not liable for business decisions made solely on the basis of AI-generated insights.
        </p>
        <div className="flex flex-wrap gap-3 mt-2">
          {[
            "✦ Powered by Gemini 2.5 Flash",
            "📍 Data from Google Places API",
            "🔒 Limited to publicly available data",
          ].map((t) => (
            <span key={t} className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>
              {t}
            </span>
          ))}
          {generatedAt && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full" style={{ background: "#FEF3C7", color: "#92400E" }}>
              🕐 Generated: {generatedAt.toLocaleTimeString("en-PH", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
      </div>
      <button
        onClick={() => setDismissed(true)}
        className="flex-shrink-0 text-[18px] leading-none"
        style={{ background: "none", border: "none", color: "#D97706", cursor: "pointer", padding: "0 2px" }}
        title="Dismiss"
      >
        ×
      </button>
    </div>
  )
}

const FooterDisclaimer = () => (
  <div className="rounded-xl px-4 py-3 border" style={{ background: C.dark, borderColor: C.border }}>
    <div className="flex items-start gap-2">
      <span className="text-[11px] flex-shrink-0" style={{ color: C.muted }}>ⓘ</span>
      <div>
        <p className="m-0 text-[11px] leading-relaxed" style={{ color: C.muted }}>
          <strong style={{ color: C.textSoft }}>Disclaimer:</strong> All insights on this dashboard are
          AI-generated using Google Gemini 2.5 Flash and are based solely on publicly available data from
          the Google Places API and owner-provided inputs. Competitor analysis, SWOT points, customer feedback
          summaries, and menu gap recommendations are structured inferences — not independently verified market
          research. Confidence labels (<span style={{ color: C.green }}>DATA-BACKED</span> ·{" "}
          <span style={{ color: C.blue }}>INFERRED</span>) indicate the basis of each insight. Validate all
          recommendations against direct business observation before making operational or financial decisions.
          This system is a decision-support tool and does not replace professional business consulting. KaPerk
          does not guarantee the accuracy, completeness, or timeliness of any AI-generated content on this page.
        </p>
        <div className="flex flex-wrap gap-4 mt-2 pt-2 border-t" style={{ borderColor: C.border }}>
          {[
            "🤖 AI Engine: Google Gemini 2.5 Flash",
            "📍 Data Source: Google Places API (Pro SKU)",
            "🚫 No web scraping used",
            "📋 RA 10173 Data Privacy Act compliant",
          ].map((t) => (
            <span key={t} className="text-[10px]" style={{ color: C.muted }}>{t}</span>
          ))}
        </div>
      </div>
    </div>
  </div>
)



export default function MarketIntelligence() {
  const [showHistory, setShowHistory] = useState(false)

  const {
      data, loading, error, lastUpdated, generate, selectedBranch, setSelectedBranch,
      range, setRange,
      history, historyLoading, selectHistoryEntry,
      refreshFromAPI, refreshing,
    } = useMarketIntelligence()

  const formatDate = (date) =>
    date
      ? date.toLocaleDateString("en-PH", {
          month: "short", day: "numeric", year: "numeric",
          hour: "2-digit", minute: "2-digit",
        })
      : "Not yet generated"

  return (
   <div className="p-6 font-sans" style={{ background: C.espresso }}>
      <SkeletonStyles />
      <div className="flex flex-col gap-5">

        {/* HEADER */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="m-0 text-[20px] font-bold" style={{ color: C.text }}>
              Market Intelligence
            </h1>
            <p className="m-0 text-[12px]" style={{ color: C.textSoft }}>
              {selectedBranch.branchName} · Synced: {formatDate(lastUpdated)}
            </p>
          </div>
          
          <div className="flex items-center gap-3 flex-wrap">
            <BranchSelector selectedBranch={selectedBranch} setSelectedBranch={setSelectedBranch} />
            <RangeSelector range={range} setRange={setRange} />
            <button
              onClick={() => setShowHistory(v => !v)}
              className="px-4 py-2 rounded-lg text-[12px] font-semibold border"
              style={{
                background: showHistory ? C.dark : C.card,
                borderColor: C.border,
                color: C.textSoft,
                cursor: "pointer",
              }}
            >
              🕓 History {history.length > 0 ? `(${history.length})` : ""}
            </button>
            <button
              onClick={generate}
              disabled={loading}
              title="Force a fresh pull from Google Places API, bypassing cached data"
              className="px-4 py-2 rounded-lg text-[12px] font-semibold border flex items-center gap-2"
              style={{
                background: loading ? C.border : C.card,
                borderColor: C.border,
                color: loading ? C.muted : C.textSoft,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {loading ? (
                <>
                  <span className="w-3 h-3 rounded-full border-2 border-current border-t-transparent animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>⟳ Refresh</>
              )}
            </button>
            <GenerateButton onClick={refreshFromAPI} loading={refreshing} label="Generate Intelligence" />
          </div>
        </div>

        {/* AI DISCLAIMER BANNER */}
        {data && <AIDisclaimerBanner generatedAt={lastUpdated} />}

        {/* HISTORY PANEL */}
        <HistoryPanel
          history={history}
          loading={historyLoading}
          lastUpdated={lastUpdated}
          onSelect={selectHistoryEntry}
          open={showHistory}
        />

        {/* ERROR */}
        {error && (
          <div
            className="rounded-xl px-4 py-3 text-[13px] border"
            style={{ background: "#FEE2E2", borderColor: "#FCA5A5", color: C.coral }}
          >
            ⚠ {error}
          </div>
        )}

        {/* KPIs */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
          <KPI label="Competitors Tracked" value={data?.kpis?.competitorsTracked ?? "—"} sub="via Google Places" color={C.amber} />
          <KPI
            label="Avg Competitor Rating"
            value={data?.kpis?.avgCompetitorRating ? `${data.kpis.avgCompetitorRating} ★` : "—"}
            sub={`Branch: ${selectedBranch.branchName.split(" ").slice(-1)}`}
            color={C.teal}
          />
          <KPI label="Feedback Collected" value={data?.kpis?.feedbackCollected ?? "—"} sub="from competitor reviews" color={C.blue} />
          <KPI label="Menu Gaps Found" value={data?.kpis?.menuGapsFound ?? "—"} sub="high opportunity items" color={C.coral} />
        </div>

        {/* COMPETITOR TRACKER + CUSTOMER FEEDBACK */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <CompetitorTracker competitors={data?.competitors} loading={loading} />
          <CustomerFeedback  feedbackSummary={data?.feedbackSummary} loading={loading} />
        </div>

        {/* SILENT LOYALTY ANALYSIS */}
        {data?.competitors && <SilentLoyaltyCard competitors={data.competitors} />}

        {/* SWOT */}
        <SwotAnalysis swot={data?.swot} loading={loading} onGenerate={generate} />

        {/* MENU GAP TABLE + CHART */}
        <MenuGapTable menuGaps={data?.menuGaps} loading={loading} />
        <OpportunityChart menuGaps={data?.menuGaps} />

        {/* FOOTER DISCLAIMER */}
        <FooterDisclaimer />

      </div>
    </div>
  )
}